// /reset-password (native sign-in environments): email, the code Entra emails, a new password, then signed in
// and on to the workspace, where a toast says the password changed. Linked from the sign-in screen's "Forgot
// password?" and the locked screen, with the email already filled in.
import { useEffect, useState } from "react";
import type { ResetPasswordCodeRequiredState, ResetPasswordPasswordRequiredState } from "@azure/msal-browser/custom-auth";
import { loadAuthConfig, loadConfig, type AuthConfig } from "../config";
import { isExpired, nativeClient, passwordRefused, passwordStrength, signInScopes, wrong, usesNativeSignIn, type NativeClient } from "../auth/native";
import { AuthActions, AuthBadge, AuthFrame, AuthHeading, authNote, authText, CodeInput, PasswordInput, StrengthBar, TextButton, useCountdown } from "../shared/AuthFrame";
import { leaveNotice } from "../shared/notice";
import { Splash } from "../shared/Splash";
import { Button, Input } from "../ui";

type Step =
  | { kind: "email" }
  | { kind: "code"; state: ResetPasswordCodeRequiredState }
  | { kind: "password"; state: ResetPasswordPasswordRequiredState };

export function ResetPasswordPage() {
  const [ready, setReady] = useState<{ native: NativeClient; auth: AuthConfig } | { failed: string } | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const portal = await loadConfig();
        const auth = await loadAuthConfig(portal.apiUrl);
        // Microsoft's pages handle resets where the portal doesn't sign in itself.
        if (!usesNativeSignIn(auth)) return window.location.replace("/");
        setReady({ native: await nativeClient(portal, auth), auth });
      } catch (error) {
        setReady({ failed: error instanceof Error ? error.message : String(error) });
      }
    })();
  }, []);
  if (!ready) return <AuthFrame />;
  if ("failed" in ready) return <Splash tone="error" text="Couldn’t open password reset" detail={ready.failed} action={<Button variant="outline" onClick={() => window.location.reload()}>Try again</Button>} />;
  return <Reset native={ready.native} scope={ready.auth.scope!} />;
}

function Reset({ native, scope }: { native: NativeClient; scope: string }) {
  const params = new URLSearchParams(window.location.search);
  // Where to go once signed in: an invitation sends people back to it.
  const next = params.get("next")?.startsWith("/") ? params.get("next")! : "/";
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resend = useCountdown(30);
  const backToSignIn = () => window.location.assign(next === "/" ? `/?email=${encodeURIComponent(email.trim())}` : next);

  const start = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const username = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(username)) return setError("Enter your work email.");
    setBusy(true);
    setError(null);
    try {
      const result = await native.resetPassword({ username });
      if (result.isCodeRequired()) { setCode(""); resend.restart(); setStep({ kind: "code", state: result.state }); }
      else if (result.error?.isUserNotFound()) setError("There’s no account with that email. Check it and try again.");
      else if (result.error?.isInvalidUsername()) setError("That doesn’t look like an email address.");
      else setError(wrong("reset start", result.error));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    if (step.kind !== "code") return;
    setBusy(true);
    setError(null);
    try {
      const result = await step.state.submitCode(value);
      if (result.isPasswordRequired()) setStep({ kind: "password", state: result.state });
      else setError(result.error?.isInvalidCode() ? "That code didn’t work. It may have expired: send a new one." : wrong("reset code", result.error));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (step.kind !== "code") return;
    setError(null);
    const result = await step.state.resendCode();
    if (result.isCodeRequired()) { setCode(""); resend.restart(); setStep({ kind: "code", state: result.state }); }
    else setError(wrong("reset resend code", result.error));
  };

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "password") return;
    if (!password) return setError("Choose a new password.");
    setBusy(true);
    setError(null);
    try {
      const result = await step.state.submitNewPassword(password);
      if (result.isCompleted()) {
        leaveNotice("Password updated");
        // Signed straight in with the new password; if that step fails, the sign-in screen takes over.
        await result.state.signIn({ scopes: signInScopes(scope) });
        window.location.assign(next);
        return;
      }
      if (isExpired(result.error)) { setStep({ kind: "email" }); return setError("That took a while, so we started over. Send yourself a new code."); }
      setError(result.error?.isInvalidPassword() ? passwordRefused : wrong("reset new password", result.error));
    } finally {
      setBusy(false);
    }
  };

  if (step.kind === "code") {
    const length = step.state.getCodeLength() || 8;
    return (
      <AuthFrame footer={<>Remembered it? <TextButton onClick={backToSignIn} style={{ fontSize: 14 }}>Back to sign in</TextButton></>}>
        <AuthBadge tone="info" icon="mail" />
        <AuthHeading title="Check your inbox" text={<>Enter the code we sent to <b style={{ color: "var(--text-strong)" }}>{email.trim()}</b> to reset your password.</>} />
        <CodeInput length={length} value={code} invalid={!!error} onChange={(value) => { setCode(value); setError(null); if (value.length === length) void submitCode(value); }} />
        {error && <p role="alert" style={{ ...authText, fontSize: 14, color: "var(--status-mismatch-fg)", marginTop: -14 }}>{error}</p>}
        <AuthActions column>
          <Button size="lg" fullWidth loading={busy} disabled={code.length < length} onClick={() => void submitCode(code)}>Continue</Button>
          <p style={authNote}>{resend.left > 0 ? `No code yet? Resend in 0:${String(resend.left).padStart(2, "0")}` : <>Nothing yet? Check spam, or <TextButton onClick={() => void resendCode()}>resend the code</TextButton></>}</p>
        </AuthActions>
      </AuthFrame>
    );
  }

  if (step.kind === "password") {
    return (
      <AuthFrame>
        <AuthHeading title="Choose a new password" text={<>For <b style={{ color: "var(--text-strong)" }}>{email.trim()}</b>. You’ll be signed in right after.</>} />
        <form noValidate onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <PasswordInput label="New password" autoComplete="new-password" autoFocus value={password} onChange={(e) => { setPassword(e.target.value); setError(null); }} error={error ?? undefined} />
            <StrengthBar score={passwordStrength(password)} />
          </div>
          <Button type="submit" size="lg" fullWidth loading={busy}>Save and sign in</Button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthBadge tone="info" icon="key-round" />
      <AuthHeading title="Forgot your password?" text="It happens. We’ll email you a code to reset it." />
      <form noValidate onSubmit={start} style={{ display: "flex", flexDirection: "column", gap: 28 }}>
        <Input size="lg" label="Work email" type="email" placeholder="you@company.com" autoComplete="email" autoFocus value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} error={error ?? undefined} />
        <AuthActions column>
          <Button type="submit" size="lg" fullWidth loading={busy}>Send code</Button>
          <p style={authNote}>Remembered it? <TextButton onClick={backToSignIn}>Back to sign in</TextButton></p>
        </AuthActions>
      </form>
    </AuthFrame>
  );
}
